import { magicLinkClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { apiBaseURL, isLandingOnly } from './landing-mode';

export const authClient = createAuthClient({
  baseURL: apiBaseURL(),
  plugins: [magicLinkClient()],
});

export const { signIn, signOut } = authClient;

/**
 * Best-effort session lookup for route `beforeLoad` hooks. Returns
 * `null` (treated as "no session") when the API is unreachable
 * instead of throwing, so a private route on a host where the
 * backend isn't live (local dev without apps/api running, or app
 * surface during a backend outage) bounces the user to /login
 * cleanly instead of crashing the router with a fetch error.
 *
 * Use this everywhere a route's `beforeLoad` needs to know the
 * session — never call `authClient.getSession()` directly inside a
 * beforeLoad.
 */
export async function getSessionSafe(): Promise<
  Awaited<ReturnType<typeof authClient.getSession>>['data']
> {
  try {
    const { data } = await authClient.getSession();
    return data;
  } catch {
    return null;
  }
}

// On landing-only hosts (athrelaris.com public surface, or any
// non-allowlisted hostname) there is no /api/auth/* function deployed
// — the better-auth session probe would hit the SPA fallback and
// either time out or hang trying to parse HTML as JSON. Short-circuit
// at module load: return a stable "no session" so __root.tsx renders
// the landing without waiting on the network.
export const useSession: typeof authClient.useSession = isLandingOnly()
  ? ((() => ({
      data: null,
      isPending: false,
      isRefetching: false,
      error: null,
      refetch: async () => null,
    })) as unknown as typeof authClient.useSession)
  : authClient.useSession;
